// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useEffect, useState } from "react";
import { App, Button, Divider, Form, Input, InputNumber, Modal, Select, Space, Switch, Typography } from "antd";

// WebhookApi wires the incoming-webhook management calls (list / create / delete).
export interface WebhookApi {
  list: () => Promise<{ id: string; name: string }[]>;
  create: (name: string) => Promise<string>; // returns the full ready-to-use URL (token embedded)
  remove: (id: string) => Promise<void>;
}

// WebhooksSection lets operators create/list/revoke a channel's incoming webhooks. A
// webhook posts server-side (so NOT end-to-end encrypted) — surfaced clearly here and via a
// "bot" badge on the messages.
function WebhooksSection({ api, open }: { readonly api: WebhookApi; readonly open: boolean }) {
  const { message } = App.useApp();
  const [hooks, setHooks] = useState<{ id: string; name: string }[]>([]);
  const [name, setName] = useState("");
  const [newURL, setNewURL] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) void api.list().then(setHooks).catch(() => undefined);
    else setNewURL("");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    setBusy(true);
    try {
      const url = await api.create(name.trim() || "Webhook");
      setNewURL(url);
      setName("");
      await navigator.clipboard?.writeText(url).catch(() => undefined);
      message.success("Webhook created — URL copied (the token isn't shown again)");
      void api.list().then(setHooks).catch(() => undefined);
    } catch (e) {
      message.error(`Couldn't create webhook: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id: string) => {
    try {
      await api.remove(id);
      setHooks((h) => h.filter((x) => x.id !== id));
    } catch (e) {
      message.error(`Couldn't delete webhook: ${(e as Error).message}`);
    }
  };

  return (
    <>
      <Divider />
      <Typography.Title level={5}>Webhooks</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
        Let an external service POST messages into this channel. Webhook messages are server-side, so they're{" "}
        <Typography.Text strong>not end-to-end encrypted</Typography.Text> (shown with a “bot” badge).
      </Typography.Paragraph>
      {hooks.map((h) => (
        <div key={h.id} className="webhook-row">
          <Typography.Text>{h.name}</Typography.Text>
          <Button size="small" danger onClick={() => remove(h.id)}>
            Delete
          </Button>
        </div>
      ))}
      <Space.Compact style={{ width: "100%", marginTop: 4 }}>
        <Input placeholder="Webhook name (e.g. GitHub)" value={name} onChange={(e) => setName(e.target.value)} onPressEnter={create} />
        <Button type="primary" loading={busy} onClick={create}>
          Create
        </Button>
      </Space.Compact>
      {newURL && (
        <Typography.Paragraph copyable={{ text: newURL }} type="secondary" style={{ fontSize: 12, marginTop: 8, wordBreak: "break-all" }}>
          POST your <Typography.Text code>{"{ \"text\": \"…\" }"}</Typography.Text> here: {newURL}
        </Typography.Paragraph>
      )}
    </>
  );
}

// RoomSettings are the channel preferences read from resolved room state (slow
// mode comes from the power levels and is threaded in separately).
export interface RoomSettings {
  topic: string;
  joinRule: string;
  historyVisibility: string;
  unlisted: boolean;
  retentionMs: number; // disappearing-messages TTL (0 = off)
  parentSpace?: string; // this room's Space (m.space.parent), if any
  chanServRegistered?: boolean; // registered with ChanServ (persistent ops)
}

const HOUR = 3_600_000;
const RETENTION_OPTIONS = [
  { value: 0, label: "Off — keep forever" },
  { value: HOUR, label: "1 hour" },
  { value: 24 * HOUR, label: "1 day" },
  { value: 7 * 24 * HOUR, label: "1 week" },
  { value: 30 * 24 * HOUR, label: "30 days" },
];

// ChannelSettings is the operator preferences panel: topic + the channel "modes"
// (access policy, history visibility, slow mode, directory listing). It computes
// the diff so only changed settings are written.
export function ChannelSettings({
  open,
  channel,
  current,
  slowMode,
  topicLock,
  canInvite,
  onMintInviteLink,
  canRegister,
  onChanServRegister,
  webhookApi,
  onClose,
  onSave,
}: {
  readonly open: boolean;
  readonly channel: string;
  readonly current: RoomSettings;
  readonly slowMode: number;
  readonly topicLock: boolean; // only operators may change the topic (IRC +t)
  readonly canInvite: boolean; // viewer is an operator (may manage invite links)
  readonly onMintInviteLink: () => Promise<string>;
  readonly canRegister?: boolean; // viewer is the founder (may register with ChanServ)
  readonly onChanServRegister?: () => void;
  readonly webhookApi?: WebhookApi; // operators only: manage incoming webhooks
  readonly onClose: () => void;
  readonly onSave: (changes: Partial<RoomSettings> & { slowMode?: number; topicLock?: boolean }) => void;
}) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [inviteLink, setInviteLink] = useState("");
  const [minting, setMinting] = useState(false);

  useEffect(() => {
    if (!open) setInviteLink(""); // don't keep a stale token shown across opens
  }, [open]);

  async function mintLink() {
    setMinting(true);
    try {
      const link = await onMintInviteLink();
      setInviteLink(link);
      await navigator.clipboard?.writeText(link).catch(() => undefined);
      message.success(inviteLink ? "Invite link rotated & copied" : "Invite link created & copied");
    } catch (e) {
      message.error(`Couldn't create invite link: ${(e as Error).message}`);
    } finally {
      setMinting(false);
    }
  }

  useEffect(() => {
    if (open) form.setFieldsValue({ ...current, slowMode, topicLock });
  }, [open, current, slowMode, topicLock, form]);

  function submit(v: RoomSettings & { slowMode: number; topicLock: boolean }) {
    const changes: Partial<RoomSettings> & { slowMode?: number; topicLock?: boolean } = {};
    if (v.topic !== current.topic) changes.topic = v.topic;
    if (v.joinRule !== current.joinRule) changes.joinRule = v.joinRule;
    if (v.historyVisibility !== current.historyVisibility) changes.historyVisibility = v.historyVisibility;
    if (v.unlisted !== current.unlisted) changes.unlisted = v.unlisted;
    if (v.retentionMs !== current.retentionMs) changes.retentionMs = v.retentionMs;
    if (v.slowMode !== slowMode) changes.slowMode = v.slowMode;
    if (v.topicLock !== topicLock) changes.topicLock = v.topicLock;
    onSave(changes);
    onClose();
  }

  return (
    <Modal title={`#${channel} settings`} open={open} onOk={() => form.submit()} onCancel={onClose} okText="Save">
      <Form form={form} layout="vertical" onFinish={submit} requiredMark={false}>
        <Form.Item name="topic" label="Topic">
          <Input.TextArea placeholder="What's this channel about?" autoSize={{ minRows: 2, maxRows: 4 }} />
        </Form.Item>
        <Form.Item name="joinRule" label="Who can join">
          <Select
            options={[
              { value: "public", label: "Public — anyone can join" },
              { value: "knock", label: "Knockable — request to join" },
              { value: "invite", label: "Invite-only" },
              ...(current.parentSpace ? [{ value: "restricted", label: "Restricted — only members of its space" }] : []),
            ]}
          />
        </Form.Item>
        <Form.Item name="historyVisibility" label="History visibility">
          <Select
            options={[
              { value: "shared", label: "Members see the full history" },
              { value: "joined", label: "Members see only after they join" },
              { value: "world_readable", label: "Anyone can read (no join needed)" },
            ]}
          />
        </Form.Item>
        <Form.Item name="slowMode" label="Slow mode (seconds, 0 = off)">
          <InputNumber min={0} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="retentionMs" label="Disappearing messages">
          <Select options={RETENTION_OPTIONS} />
        </Form.Item>
        <Form.Item name="unlisted" label="Secret (hide from the channel directory)" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="topicLock" label="Topic-locked (only operators can change the topic)" valuePropName="checked">
          <Switch />
        </Form.Item>
        {canInvite && (
          <Form.Item label="Invite link (IRC +k)">
            <Button onClick={mintLink} loading={minting} block>
              {inviteLink ? "Rotate invite link" : "Create invite link"}
            </Button>
            {inviteLink && (
              <Input.TextArea
                value={inviteLink}
                readOnly
                autoSize
                onFocus={(e: React.FocusEvent<HTMLTextAreaElement>) => e.target.select()}
                style={{ marginTop: 8, fontSize: 12 }}
              />
            )}
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Set "Who can join" to Invite-only so the link is the only way in. Rotating kills older links.
            </Typography.Text>
          </Form.Item>
        )}
        {canRegister && onChanServRegister && (
          <Form.Item label="ChanServ (persistent ops)">
            {current.chanServRegistered ? (
              <Typography.Text type="success">Registered — operator/voice access persists across rejoins.</Typography.Text>
            ) : (
              <>
                <Button onClick={onChanServRegister} block className="cs-register">
                  Register with ChanServ
                </Button>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Lets you grant auto-op/voice that survives a member leaving and rejoining.
                </Typography.Text>
              </>
            )}
          </Form.Item>
        )}
        {canInvite && webhookApi && <WebhooksSection api={webhookApi} open={open} />}
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Topic and these modes are operator-only and apply room-wide.
        </Typography.Text>
      </Form>
    </Modal>
  );
}
